import React, { useLayoutEffect, useRef, useState } from 'react';
import { Body, Button, Colors, Title } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import styled, { css } from 'styled-components/macro';
import {
  getAllMilestoneCollections,
  getAllOpenCollections,
  getMilestoneIconType,
  getMilestoneProgress,
  getMilestoneStatus,
  MilestoneStatus,
  term,
} from 'utils/project-ruleset';
import { Milestone, MilestoneType } from 'types/dt-graphql-apollo-v2';
import { useAccessControl } from 'hooks/useAccessControl';
import { groupedPermissionsList } from 'store/permissions';

type MilestoneCardsProps = {
  milestones: Milestone[];
  changeMilestone: (id: string) => void;
  onCreateNewMilestone: VoidFunction;
  onCreateNewMilestoneWithType: (type: MilestoneType) => void;
  selectedMilestoneId?: string;
};

export const MilestoneCards = ({
  milestones,
  changeMilestone,
  onCreateNewMilestone,
  onCreateNewMilestoneWithType,
  selectedMilestoneId,
}: MilestoneCardsProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);

  const hasAccess = useAccessControl();
  const canCreateCollection = hasAccess(
    groupedPermissionsList.CAN_CREATE_COLLECTION
  );

  useLayoutEffect(() => {
    function updateIsScrollable() {
      if (ref?.current) {
        setIsScrollable(ref?.current?.scrollWidth > ref?.current?.clientWidth);
      }
    }
    window.addEventListener('resize', updateIsScrollable);
    updateIsScrollable();
    return () => window.removeEventListener('resize', updateIsScrollable);
  }, []);

  const filteredStaticMilestones = getAllOpenCollections(milestones) || [];

  const filteredMilestones = getAllMilestoneCollections(milestones) || [];

  const renderMilestoneCard = (milestone: Milestone) => {
    const milestoneIconType = getMilestoneIconType(milestone, milestones);
    const milestoneStatus = getMilestoneStatus(milestones, milestone);
    return (
      <StyledCard
        key={`card-${milestone.externalId}`}
        isActive={milestone.externalId === selectedMilestoneId}
        onClick={() => changeMilestone(milestone.externalId)}
      >
        <StyledCardContent
          hasIcon={!!milestoneIconType}
          status={milestoneStatus}
        >
          {milestoneIconType && (
            <StyledCardIcon>
              <Icon type={milestoneIconType} />
            </StyledCardIcon>
          )}

          <StyledCardTitle level={5}>{milestone.name}</StyledCardTitle>
          <StyledCardStatus level={2}>{milestoneStatus}</StyledCardStatus>
        </StyledCardContent>
        <StyledCardProgressBar
          progress={getMilestoneProgress(milestones, milestone)}
        />
      </StyledCard>
    );
  };

  const renderStaticMilestoneCards = () =>
    filteredStaticMilestones?.map(renderMilestoneCard);

  const renderMilestoneCards = () =>
    !!filteredMilestones.length && (
      <StyledMilestoneCards>
        {filteredMilestones.map(renderMilestoneCard)}
        {canCreateCollection && (
          <StyledMilestoneAddButton
            icon="AddLarge"
            aria-label={`Create ${term.milestone}`}
            onClick={() => onCreateNewMilestoneWithType(MilestoneType.Default)}
          />
        )}
      </StyledMilestoneCards>
    );

  return (
    <StyledScrollContainer isScrollable={isScrollable}>
      <Container ref={ref}>
        <StyledCards>
          {canCreateCollection && (
            <StyledCardButton onClick={onCreateNewMilestone} icon="AddLarge">
              Create {term.collection}
            </StyledCardButton>
          )}

          {renderStaticMilestoneCards()}

          {renderMilestoneCards()}
        </StyledCards>
      </Container>
    </StyledScrollContainer>
  );
};

const StyledScrollContainer = styled.div<{ isScrollable: boolean }>`
  position: relative;
  width: 100%;
  padding: 4px 0;
  ${({ isScrollable }) =>
    isScrollable &&
    `
    &:after {
      position: absolute;
      top: 0;
      bottom: 0;
      right: -8px;
      box-shadow: inset -10px 0 8px -8px rgb(0 0 0 / 15%);
      width: 30px;
      content: '';
      pointer-events: none;
    }  
  `}
`;

const Container = styled.div`
  display: flex;
  flex: 1;
  width: calc(100% + 16px);
  margin: -8px -8px;
  padding: 8px 8px;
  overflow-x: auto;
`;

const StyledCards = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  flex: 1;
`;

const StyledBaseCard = css`
  border: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;
  padding: 0.75rem 1rem;
  width: 184px;
  height: 72px;
  background: ${Colors['decorative--grayscale--white']};
  position: relative;
  box-shadow:
    0 0 2px 1px rgba(0, 0, 0, 0.04),
    0 3px 8px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  &:hover {
    cursor: pointer;
  }
`;

const StyledCard = styled.div<{ isActive?: boolean }>`
  ${StyledBaseCard};
  border: 0;
  ${({ isActive }) =>
    isActive &&
    `
    border:1px solid var(--cogs-primary) !important;
    background: var(--cogs-bg-selected);
    padding: calc(0.75rem - 1px) calc(1rem - 1px);
    ${StyledCardProgressBar} {
      height: 3px;
    }
  `};
`;

const StyledCardButton = styled(Button)`
  ${StyledBaseCard};
  flex-direction: row;
  align-items: center;
  width: auto;
  color: var(--cogs-primary);
  border: 1px dashed var(--cogs-border-default);
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  min-width: 64px;
  box-shadow: none;
`;

const StyledCardTitle = styled(Title)`
  font-weight: 500;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledMilestoneCards = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0;
  box-shadow:
    0 0 2px 1px rgba(0, 0, 0, 0.04),
    0 3px 8px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  overflow: hidden;
  ${StyledCard} {
    border-radius: 0;
    box-shadow: none;
    border-left: 1px solid var(--cogs-border-default);
    &:first-child {
      border-radius: 8px 0 0 8px;
      border-left: 0;
    }
  }
`;

const StyledMilestoneAddButton = styled(StyledCardButton)`
  border-radius: 0 8px 8px 0;
  box-shadow: none;
`;

const StyledCardProgressBar = styled.div<{ progress?: number | null }>`
  width: ${({ progress }) => progress || 0}%;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--cogs-primary);
`;

const StyledCardIcon = styled.div`
  grid-row-start: 1;
  grid-row-end: 3;
  padding: 2px 0;
`;

const StyledCardStatus = styled(Body)`
  color: var(--cogs-text-hint);
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledCardContent = styled.div<{
  hasIcon?: boolean;
  status: MilestoneStatus | null;
}>`
  display: grid;
  grid-template-columns: ${({ hasIcon }) => (hasIcon ? '20px auto' : 'auto')};
  gap: 0.25rem 0.5rem;
  ${({ status }) =>
    status === MilestoneStatus.ACTIVE &&
    `
    ${StyledCardStatus}, ${StyledCardIcon} {
      color: var(--cogs-primary) !important;
    }`};
  ${({ status }) =>
    status === MilestoneStatus.ARCHIVED &&
    `
    ${StyledCardIcon} {
      color: var(--cogs-primary) !important;
    }`};
`;
